import { inject, Injectable } from '@angular/core';
import { BehaviorSubject, combineLatest, Observable } from 'rxjs';
import { map, startWith } from 'rxjs/operators';

import { CartStoreRefiner } from '@common/refiners/cart.refiner';
import { ShopStoreRefiner } from '@common/refiners/shop.refiner';
import { ShopStoreSelector } from '@common/selectors/shop.selector';
import { MarketplaceStoreSelector } from '@common/selectors/marketplace.selector';
import {
  EligiblePromotionsLabelsService,
  mergePromotionLabelsIntoTree,
} from '@core/services/eligible-promotions-labels/eligible-promotions-labels.service';
import { ProductKeys } from '@config/keys/product.keys';
import { IProductLabel, TreeCategoryWithProduct } from '@core/models/product.model';
import { collectCatalogFilterOptions, filterTreeByCatalogFilters } from './catalog-filters';
import { catalogTreeMatchesShop } from './catalog-tree-shop';
import { ShopCatalogFiltersService } from './shop-catalog-filters.service';
import { TreeTab } from '@core/models/tab-list.model';
import { flattenTreeIds, treeListToTreeTabList } from '@shared/utils/tree/tree-to-tree-tabs';

import {
  ShopProductListBlockDataSource,
  SHOP_PRODUCT_LIST_BLOCK_LOADING_VM,
  ShopProductListBlockViewModel,
} from './shop-product-list-block-view-model';

@Injectable()
export class ShopProductListBlockStoreDataSource implements ShopProductListBlockDataSource {
  private readonly shopSelector = inject(ShopStoreSelector);
  private readonly shopRefiner = inject(ShopStoreRefiner);
  private readonly cartRefiner = inject(CartStoreRefiner);
  private readonly marketplaceSelector = inject(MarketplaceStoreSelector);
  private readonly eligiblePromotionsLabelsService = inject(EligiblePromotionsLabelsService);
  private readonly catalogFilters = inject(ShopCatalogFiltersService);

  private readonly catalogFiltersEnabled$ = new BehaviorSubject(false);

  private readonly filterColors$ =
    this.marketplaceSelector.selectMarketPlaceProductLabelsFilterColors;
  private readonly tagColors$ = this.marketplaceSelector.selectMarketPlaceProductLabelsTagColors;

  private readonly selectedLabel$ = new BehaviorSubject<IProductLabel | undefined>(undefined);

  private readonly treeTabsOfCatalogCategory: Observable<TreeTab[]> =
    this.shopRefiner.selectActiveShopEnabledTreeCatalogCategory.pipe(
      map(treeCatalogueCategories =>
        treeListToTreeTabList({
          trees: treeCatalogueCategories,
          entityToTab: ({ category }) => ({
            id: category.id,
            name: category.name,
            icon: category.icon,
          }),
        }),
      ),
    );

  private filterCategoriesBySelectedLabel(
    tree: TreeCategoryWithProduct[],
    selectedLabel: IProductLabel | undefined,
  ): TreeCategoryWithProduct[] {
    if (!selectedLabel) {
      return tree;
    }

    return tree
      .map((categoryWithProduct: TreeCategoryWithProduct) => {
        const filteredProducts = categoryWithProduct.products.filter(product =>
          product[ProductKeys.Labels]?.some(label => label.id === selectedLabel.id),
        );
        return {
          ...categoryWithProduct,
          products: filteredProducts,
          children: this.filterCategoriesBySelectedLabel(
            categoryWithProduct.children,
            selectedLabel,
          ),
        } satisfies TreeCategoryWithProduct;
      })
      .filter(categoryWithProduct => categoryWithProduct.products.length > 0);
  }

  private readonly catalogFilterOptions$ = this.shopRefiner.selectActiveShopProducts.pipe(
    map(products => collectCatalogFilterOptions(products)),
  );

  private readonly filteredCategories$ = combineLatest({
    categoriesWithProducts: this.shopRefiner.selectActiveProductsCategories,
    promotionLabelsMap: this.eligiblePromotionsLabelsService.getPromotionLabelsByProductId$(),
    selectedLabel: this.selectedLabel$,
    appliedFilters: this.catalogFilters.applied$,
    catalogFiltersEnabled: this.catalogFiltersEnabled$,
  }).pipe(
    map(
      ({
        categoriesWithProducts,
        promotionLabelsMap,
        selectedLabel,
        appliedFilters,
        catalogFiltersEnabled,
      }) => {
        const treeWithPromotionLabels = mergePromotionLabelsIntoTree(
          categoriesWithProducts,
          promotionLabelsMap,
        );
        const categoriesBeforeCatalogFilters = this.filterCategoriesBySelectedLabel(
          treeWithPromotionLabels,
          selectedLabel,
        );
        const filters = catalogFiltersEnabled ? appliedFilters : [];
        return {
          categoriesBeforeCatalogFilters,
          categoriesWithProductsFilteredByLabel: filterTreeByCatalogFilters(
            categoriesBeforeCatalogFilters,
            filters,
          ),
        };
      },
    ),
  );

  readonly vm$: Observable<ShopProductListBlockViewModel> = combineLatest({
    shop: this.shopSelector.selectActive$,
    currentPageShop: this.shopSelector.selectCurrentPage$,
    isShopLoading: this.shopSelector.selectIsLoading$,
    cartProducts: this.cartRefiner.selectActiveCartProducts,
    tagColors: this.tagColors$,
    filterColors: this.filterColors$,
    treeTabsOfCatalogCategory: this.treeTabsOfCatalogCategory,
    selectedLabel: this.selectedLabel$,
    filteredCategories: this.filteredCategories$,
    catalogFilterOptions: this.catalogFilterOptions$,
  }).pipe(
    map(({ filteredCategories, shop, currentPageShop, isShopLoading, ...vm }) => {
      const categoriesWithProductsFilteredByLabel =
        filteredCategories.categoriesWithProductsFilteredByLabel;
      const activeShopId = shop?.id;
      // The category tree is debounced and stays on the previous shop after the
      // active shop (and the loading flag) have already switched.
      const catalogIsStale =
        !!activeShopId &&
        !catalogTreeMatchesShop(categoriesWithProductsFilteredByLabel, activeShopId);
      const pageShopMismatch = currentPageShop?.id !== activeShopId;
      const hideCatalog = isShopLoading || pageShopMismatch || catalogIsStale;

      if (hideCatalog) {
        return {
          ...vm,
          shop: null,
          isShopLoading: true,
          treeTabsOfCatalogCategory: [],
          categoriesBeforeCatalogFilters: [],
          categoriesWithProductsFilteredByLabel: [],
          categoriesId: [],
        };
      }

      return {
        ...vm,
        shop,
        isShopLoading,
        categoriesBeforeCatalogFilters: filteredCategories.categoriesBeforeCatalogFilters,
        categoriesWithProductsFilteredByLabel,
        categoriesId: flattenTreeIds(categoriesWithProductsFilteredByLabel),
      };
    }),
    startWith(SHOP_PRODUCT_LIST_BLOCK_LOADING_VM),
  );

  setCatalogFiltersEnabled(enabled: boolean): void {
    if (this.catalogFiltersEnabled$.value === enabled) {
      return;
    }
    this.catalogFiltersEnabled$.next(enabled);
  }

  selectLabel(label: IProductLabel | undefined): void {
    const current = this.selectedLabel$.value;
    if (current === label) {
      this.selectedLabel$.next(undefined);
      return;
    }
    this.selectedLabel$.next(label);
  }
}
